import { addMilliseconds, addMinutes, isPast } from "date-fns";
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports
import { toast } from "react-toastify";
import { ShareDBSourceOptions } from "sharedb";
import { Callback, Doc, Op } from "sharedb/lib/client";

import { ShareDBContext } from "../providers/ShareDBProvider";
import { AuthError, checkUserCanPerformOperation } from "../shared/auth";
import { AUTH_ERRORS, WebsocketErrorType } from "../shared/types/websocket-auth";
import { ExtendedDoc, ExtendedOp, WorksheetType } from "../shared/types/worksheet";
import { DATA_PATH } from "../shared/worksheets/constants";
import { accessIdHasData, hasBlocks } from "../shared/worksheets/doc-helpers";
import { createIncrementVersionQueue } from "../worksheets/field-versioning";

import {
  clearSubmissionTrackerById,
  createSubmissionTracker,
  getOperationId,
  getRelatedOperationIds,
  handleSubmissionComposed,
  reconcileSubmissionTrackersForDoc,
  updateSubmissionTracker,
} from "./submission-tracker";
import {
  enrichShareDBFailureLog,
  logUncommittedOpsDiscardedByDocRefresh,
  reportUncommittedWorksheetFieldTextLoss,
  trackLocalOpFieldTextAfterApply,
  trackLocalOpFieldTextBeforeApply,
} from "./worksheet-field-content-client-log";

import { t } from "i18n/i18n";
import { usePageCapabilitiesForPageId } from "store/capability-hooks";
import { store, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentUserId,
  selectPageCapabilities,
  selectPageGroupIdForCurrentUserByPageId,
  selectPageIdByWorksheetId,
  selectSectionCapabilities,
  selectWorksheetIdByPage,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { isTestEnv } from "utils/env";
import { logInfoWithDebugFlag, rollbarAndLogError, rollbarAndLogWarning } from "utils/logger";
import { getUpdateDraftPublicationFunction } from "utils/publishing";

/**
 * Determines a human-readable reason for a client-side doc error.
 * Used to enrich error logs for diagnosis.
 *
 * - "client_server_mismatch": client still has permission at error time but
 *   server denied
 * - "permission_revoked_after_<action>": client had permission when the request
 *   was initiated, but an update removed it before the error callback fired.
 * - "no_page_permissions": client has zero page permissions — user has no
 *   enrollment or no released pages in this section.
 */
const getDocErrorReason = (
  docId: string,
  hadPermissionAtRequest: boolean,
  action: "subscribe" | "fetch",
) => {
  const state = store.getState();
  const pageId = selectPageIdByWorksheetId(state, docId);
  const { canViewPage } = selectPageCapabilities(state, pageId);

  if (canViewPage) return "client_server_mismatch";
  if (hadPermissionAtRequest) return `permission_revoked_after_${action}`;
  return "no_page_permissions";
};

// ============================================================================
// Constants
// ============================================================================

// This sets how many listeners we can register for a single EventEmitter.
// Doc and DocPresence are both EventEmitters, and the default is 10.
// If the max number is exceeded, we just get a warning, nothing breaks.
// For our implementation, each Field listens to the doc individually,
// so we expect this number to be quite a lot higher than the default.
// The current number is chosen so that we likely get a warning if something actually goes
// wrong, e.g. listeners get added 5-10x more often than expected.
export const MAX_DOC_LISTENERS = 256;
export const MAX_RETRIES = 100;
export const MAX_MINUTES_IN_FLIGHT = 10;
export const MINIMUM_TIME_BETWEEN_REQUESTS_MS = 1000;
export const MAX_RETRY_DELAY_MS = 30000;
export const DELAYED_SAVE_WARNING_THRESHOLD_MS = 10000;

// ============================================================================
// Core Infrastructure
// ============================================================================

export type DocCache = Record<string, ExtendedDoc> & {
  /** Latest worksheet doc. Used by AI tools. */
  current?: ExtendedDoc;
};

export const docCache = {} as DocCache;
const cacheCallbacks: Record<string, Set<() => void>> = {};

const cacheHasNoListeners = (docId: string) => {
  const callbacks = cacheCallbacks[docId];
  return !callbacks || callbacks.size === 0;
};

/**
 * We use a small custom event emitter system to allow multiple components to listen to changes
 * originating from a single ShareDB document, without re-running side effects.
 */
const triggerDocUpdate = (docId: string) => {
  const callbacks = cacheCallbacks[docId];
  if (callbacks) {
    callbacks.forEach((cb) => cb());
  }
};

/** Keep submission trackers in sync with ShareDB's send queue after each doc op. */
const updateSubmissionTrackerState = (doc: ExtendedDoc, ops: ExtendedOp[], stage: string) => {
  const op = ops[0];
  // Local submit batches carry operationId; remote batches do not — reconcile handles those.
  if (op?.operationId) {
    updateSubmissionTracker(op.operationId, doc.pendingOps, stage);
  }
  reconcileSubmissionTrackersForDoc(doc, stage);
};

const listenToDocUpdates = (docId: string, callback: () => void) => {
  if (!cacheCallbacks[docId]) {
    cacheCallbacks[docId] = new Set();
  }
  const callbacks = cacheCallbacks[docId];
  callbacks.add(callback);
  return () => {
    callbacks.delete(callback);
  };
};

const checkOpPermissions = (doc: Doc<WorksheetType>, docOp: Op | Op[]) => {
  const state = store.getState();
  const currentUserId = selectCurrentUserId(state) ?? state.auth.websocket.user_id;
  const { canAuthorSection } = selectSectionCapabilities(state);
  const pageId = selectPageIdByWorksheetId(state, doc.id);
  const { canViewPage } = selectPageCapabilities(state, pageId);
  const pageGroupId = selectPageGroupIdForCurrentUserByPageId(state, pageId);

  try {
    const ops = Array.isArray(docOp) ? docOp : [docOp];

    for (const op of ops) {
      // Check if we have permissions
      checkUserCanPerformOperation(
        currentUserId,
        canAuthorSection,
        canViewPage,
        [pageGroupId].filter(Boolean) as string[],
        op,
        doc.data,
        true,
      );
    }
  } catch (err) {
    rollbarAndLogError("[ShareDBDoc] Failed client side op permission check", err, {
      op: JSON.stringify(docOp),
      docId: doc.id,
      pageId,
      currentUserId,
      canAuthorSection,
      canViewPage,
      pageGroupId,
    });
    // We ignore overwrite errors, as we assume the remote state (where the data exists),
    // is the correct one, and will be synced to the local state soon.
    if (!(err instanceof AuthError) || err.message !== AUTH_ERRORS.OVERWRITE_DISALLOWED) {
      throw err;
    }
  }
};

// If we want to refresh the doc because e.g. the permissions changed on the backend,
// but the doc is already cached by ShareDB, we need to fetch and re-ingest the data manually.
// Just calling connection.get(docId) will return the locally cached version.
//
// ShareDB doesn't have a function to re-fetch data, which is why we manually
// interfere with the ShareDB doc's internal state to make it believe a
// re-sync is necessary. This is brittle and mostly undocumented.
// I made this by looking at the ShareDB internal code and seeing what
// might be able to cause a refresh.
export const forceRefreshDoc = (docId: string, callback?: (err?: Error) => void) => {
  const cachedDoc = docCache[docId];
  if (!cachedDoc) return;

  logUncommittedOpsDiscardedByDocRefresh(cachedDoc, "forceRefreshDoc");

  const docPrototype = cachedDoc as unknown as {
    _setType: (type: string) => void;
    version: number;
    _fetch: (options: { force: boolean }, cb: (err: Error) => void) => void;
  };
  // From reading the source code, these are the necessary changes to force a refresh.
  (docPrototype as { version: number | null }).version = null;
  (docPrototype as { _setType: (t: string | null) => void })._setType(null);

  const state = store.getState();
  const pageId = selectPageIdByWorksheetId(state, docId);
  const { canViewPage } = selectPageCapabilities(state, pageId);
  docPrototype._fetch({ force: true }, (err) => {
    if (err) {
      const websocket = store.getState().auth.websocket;
      const reason = getDocErrorReason(docId, canViewPage, "fetch");
      rollbarAndLogError("[ShareDBDoc] Error fetching doc", {
        docId,
        user_id: websocket?.user_id,
        err: err.message,
        reason,
      });
    }
    triggerDocUpdate(docId);
    if (callback) {
      callback(err);
    }
  });
};

const { incrementVersion, flushQueue } = createIncrementVersionQueue(docCache);

/**
 * Extracts caller information from stack trace for debugging.
 * TODO: move this to a shared utility function and use it in other places.
 */
const getCallerInfo = (): string | undefined => {
  try {
    const stack = new Error().stack;
    if (!stack) return undefined;
    const lines = stack.split("\n");
    // Skip: Error, getCallerInfo, useWorksheet, so we want index 3 or 4
    // Look for the first line that's not from ShareDBDoc.tsx
    for (let i = 3; i < Math.min(lines.length, 10); i += 1) {
      const line = lines[i];
      if (line && !line.includes("ShareDBDoc.tsx")) {
        // Extract function/component name
        const match = line.match(/at\s+(\w+)/);
        if (match) {
          return match[1];
        }
      }
    }
  } catch {
    // Ignore errors in stack trace parsing
  }
  return undefined;
};

// ============================================================================
// Core useWorksheet Hook
// ============================================================================

/**
 * useWorksheet will connect to a remote ShareDB document and return the doc object,
 * and will also force a re-render when any of the document data changes.
 */
type CheckIfRerenderIsNeededFn = (
  doc: ExtendedDoc | null,
  previousDoc: ExtendedDoc | null,
) => boolean;

export const useWorksheet = (
  docId: string | undefined | null,
  checkIfRerenderIsNeeded?: CheckIfRerenderIsNeededFn,
) => {
  // Store caller info for logging purposes in a ref so it's computed once and doesn't need to be in dependencies
  const callerRef = useRef<string | undefined>(undefined);
  if (callerRef.current === undefined) {
    callerRef.current = getCallerInfo();
  }
  const [doc, setDoc] = useState<ExtendedDoc | null>(null);
  const [, rerenderComponent] = useState({});
  const dispatch = useAppDispatch();
  const previousDataStateRef = useRef<"ready" | "undefined" | null>(null);
  const previousDocRef = useRef<ExtendedDoc | null>(null);
  const currentDocRef = useRef<ExtendedDoc | null>(null);

  // Reset state tracking when docId changes
  useEffect(() => {
    previousDataStateRef.current = null;
    previousDocRef.current = null;
    currentDocRef.current = null;
  }, [docId]);

  // Keep currentDocRef in sync with doc state
  useEffect(() => {
    currentDocRef.current = doc;
  }, [doc]);

  const maybeRerender = useCallback(() => {
    if (!docId) {
      return;
    }
    // For the case where we listen to a doc before it exists, we need to check if we've loaded
    // it by the time we receive an update.
    const cachedDoc = docCache[docId];
    const currentDoc = cachedDoc || currentDocRef.current;

    // Check if we should re-render based on the check function
    if (checkIfRerenderIsNeeded) {
      const shouldRerender = checkIfRerenderIsNeeded(currentDoc, previousDocRef.current);
      if (!shouldRerender) {
        // Don't re-render, but still update the doc ref
        setDoc((existingDoc) => existingDoc || cachedDoc);
        previousDocRef.current = currentDoc;
        return;
      }
    }

    // Re-render (either no check function provided, or check returned true)
    rerenderComponent({});
    logInfoWithDebugFlag(
      "debug_sharedb_subscriptions",
      `[useWorksheet] Rerendering with cached doc`,
      {
        docId,
        cachedDocId: cachedDoc?.id,
        caller: callerRef.current,
      },
    );
    setDoc((existingDoc) => existingDoc || cachedDoc);
    previousDocRef.current = currentDoc;
  }, [docId, checkIfRerenderIsNeeded]);

  const shareDBContext = useContext(ShareDBContext);
  const shareDBConnection = shareDBContext?.shareDBConnection ?? null;
  const shareDBSectionId = shareDBContext?.shareDBSectionId ?? "";

  const updateDraftPublication = getUpdateDraftPublicationFunction(shareDBSectionId);

  useEffect(() => {
    // We need to ensure we wait until we have a ShareDB connection
    // The connection only exists within a course context (ShareDBProvider is in [courseId].tsx)
    if (!docId || !shareDBConnection || !shareDBSectionId) {
      logInfoWithDebugFlag(
        "debug_sharedb_subscriptions",
        `[useWorksheet] Not subscribing to doc because missing required context`,
        {
          docId,
          shareDBConnection: shareDBConnection?.state,
          shareDBSectionId,
          caller: callerRef.current,
        },
      );

      return;
    }

    logInfoWithDebugFlag(
      "debug_sharedb_subscriptions",
      `[useWorksheet] Subscribing to doc ${docId}`,
      {
        docId,
        shareDBConnection: shareDBConnection?.state,
        shareDBSectionId,
        caller: callerRef.current,
      },
    );
    const stopListening = listenToDocUpdates(docId, maybeRerender);
    const unsubscribe = () => {
      // Any caller of useWorksheet will listen to a global event emitter for changes to the doc,
      // and the last caller to unsubscribe will actually unsubscribe the doc from the remote server.
      const docToUnsubscribe = docCache[docId];
      stopListening();
      const cleanupSubscriptionAndDoc = () => {
        const shouldStillUnsubscribe = cacheHasNoListeners(docId);
        if (shouldStillUnsubscribe) {
          logInfoWithDebugFlag(
            "debug_sharedb_subscriptions",
            `[useWorksheet] Unsubscribing from doc`,
            {
              docId,
              caller: callerRef.current,
            },
          );
          flushQueue();
          delete docCache[docId];
          if (docToUnsubscribe?.localPresence) {
            docToUnsubscribe.localPresence.destroy();
          }
          if (docToUnsubscribe?.presence) {
            docToUnsubscribe.presence.unsubscribe();
          }
          if (docToUnsubscribe) {
            docToUnsubscribe.destroy();
          }
        }
      };
      const shouldReallyUnsubscribe = cacheHasNoListeners(docId);
      if (shouldReallyUnsubscribe) {
        // There are sometimes race condition, where we unsubscribe from a doc, and while we're waiting
        // for server confirmation, the client wants to be subscribed to the doc again (which doesn't send any messages)
        // but the server then processes the unsubscribe request erroneously. As a quick hack,
        // we wait for some time before actually unsubscribing.
        if (isTestEnv) {
          // Tests don't do well with the timeout, as the cleanup of one test
          // might affect the next test, so we disable the timeout in tests.
          cleanupSubscriptionAndDoc();
        } else {
          setTimeout(cleanupSubscriptionAndDoc, 500);
        }
      }

      setDoc(null);
    };

    // The ShareDB code doesn't handle the case well where the same docId is requested
    // multiple times. We cache the instance and make sure future hook calls for the same ID
    // return the same instance, and share the same root listeners as whatever the first instance
    // was that called useWorksheet for that ID.
    const cachedDoc = docCache[docId];
    if (cachedDoc) {
      logInfoWithDebugFlag("debug_sharedb_subscriptions", `[useWorksheet] Using cached doc`, {
        docId,
        caller: callerRef.current,
      });
      setDoc(cachedDoc);
      return unsubscribe;
    }

    const newDoc: ExtendedDoc = shareDBConnection.get(
      window._env_.SHAREDB_WORKSHEET_COLLECTION,
      docId,
    ) as ExtendedDoc;
    logInfoWithDebugFlag("debug_sharedb_subscriptions", `[useWorksheet] Fetched new doc`, {
      docId,
      caller: callerRef.current,
    });
    setDoc(newDoc);

    docCache[docId] = newDoc;

    const presence = shareDBConnection.getDocPresence(
      window._env_.SHAREDB_WORKSHEET_COLLECTION,
      docId,
    );
    newDoc.presence = presence;
    newDoc.localPresence = presence.create();
    presence.subscribe();
    newDoc.presence.setMaxListeners(MAX_DOC_LISTENERS);

    const onError = (err: Error) => {
      rollbarAndLogError("[ShareDBDoc] Error during doc action", { err: err?.message, docId });
    };

    const onOp = () => {
      triggerDocUpdate(docId);
    };

    const onDel = () => unsubscribe();

    newDoc.on("op", onOp);
    newDoc.on("load", onOp);
    newDoc.on("del", onDel);
    newDoc.on("error", onError);
    newDoc.setMaxListeners(MAX_DOC_LISTENERS);

    // Add event listeners for submission tracking and field text loss detection
    newDoc.on("before op batch", (ops: ExtendedOp[]) =>
      updateSubmissionTrackerState(newDoc, ops, "beforeOpBatch"),
    );
    newDoc.on("before op", (ops: ExtendedOp[], source: unknown) => {
      updateSubmissionTrackerState(newDoc, ops, "beforeOp");
      // ShareDB uses source === false for server-applied ops; only track local submissions.
      if (newDoc.data && source !== false) {
        trackLocalOpFieldTextBeforeApply(newDoc.id, newDoc.data, ops);
      }
    });
    newDoc.on("op", (ops: ExtendedOp[], source: unknown) => {
      updateSubmissionTrackerState(newDoc, ops, "op");
      if (newDoc.data && source !== false) {
        trackLocalOpFieldTextAfterApply(newDoc.data, ops);
      }
    });

    if (!newDoc.__hardRollback) {
      // Override _hardRollback to track failures
      newDoc.__hardRollback = newDoc._hardRollback;

      // This event can happen on an op submission, but when it does it rolls back the submission
      // without sending it to the server and doesn't necessarily log anything.  We are patching it
      // here so we can at least log it (and disable the submission tracker as we know it failed).
      newDoc._hardRollback = function _hardRollback(this: ExtendedDoc, error: Error) {
        const operationId = getOperationId(this.inflightOp);
        const state = store.getState();

        if (newDoc.__hardRollback) {
          newDoc.__hardRollback.call(this, error);
        }

        const fieldLosses =
          this.data &&
          reportUncommittedWorksheetFieldTextLoss({
            docId: this.id,
            doc: this.data,
            operationId,
            relatedOperationIds: getRelatedOperationIds(operationId),
            cause: "rollback",
            userId: selectCurrentUserId(state),
            sectionId: shareDBSectionId,
            errorMessage: error.message,
          });

        rollbarAndLogError(
          "ShareDB _hardRollback called - operation failed",
          enrichShareDBFailureLog(
            {
              docId: this.id,
              error: error.message,
              errorStack: error.stack,
              connectionState: this.connection?.state,
              canSend: this.connection?.canSend,
              operationId,
            },
            fieldLosses ?? [],
          ),
        );

        if (operationId) {
          clearSubmissionTrackerById(operationId, { cause: "rollback" });
        }
        reconcileSubmissionTrackersForDoc(this, "hardRollback");
      };
    }

    // The flush mechanism is not resending ops if they never get acknowledged.
    // We are patching here to do that but also adding a timeout to ensure
    // we don't wait indefinitely for an acknowledgment that may never come.
    // Here's what the original flush method looks like:
    // ```
    // // Send the next pending op to the server, if we can.
    // //
    // // Only one operation can be in-flight at a time. If an operation is already on
    // // its way, or we're not currently connected, this method does nothing.
    // Doc.prototype.flush = function () {
    //   // Ignore if we can't send or we are already sending an op
    //   if (!this.connection.canSend || this.inflightOp) return;
    //   // Send first pending op unless paused
    //   if (!this.paused && this.pendingOps.length) {
    //     this._sendOp();
    //   }
    // };
    // ```
    if (!newDoc._flush) {
      newDoc._flush = newDoc.flush;
      newDoc.flush = function flush(this: ExtendedDoc) {
        // Ignore if we can't send
        if (!this.connection.canSend) return;

        // Send first pending op unless paused
        if (!this.paused && (this.pendingOps.length || this.inflightOp)) {
          // Here we add some logic to detect if the op is stuck in flight too long
          if (this.inflightOp) {
            // Calculate exponential backoff delay: 1s, 2s, 4s, 8s, 16s, etc.
            const retryDelay = Math.min(
              MINIMUM_TIME_BETWEEN_REQUESTS_MS * 2 ** this.inflightOp.retries,
              MAX_RETRY_DELAY_MS,
            ); // Cap at 30 seconds
            // If we're inflight and it hasn't been long enough based on retry count, skip resending
            if (
              this.inflightOp.sentAt &&
              !isPast(addMilliseconds(this.inflightOp.sentAt, retryDelay))
            ) {
              return;
            }

            if (!this.inflightOp.originalSentAt) {
              this.inflightOp.originalSentAt = this.inflightOp.sentAt;

              // The first time we get here set a timeout
              setTimeout(() => {
                // Check if its the same inflight op
                if (
                  this.inflightOp &&
                  isPast(
                    addMilliseconds(
                      this.inflightOp.originalSentAt,
                      DELAYED_SAVE_WARNING_THRESHOLD_MS,
                    ),
                  )
                ) {
                  toast.error(t("error.toasts.save_delay"));
                }
              }, DELAYED_SAVE_WARNING_THRESHOLD_MS);
            }

            rollbarAndLogWarning("ShareDB op stuck in flight - resending", {
              originalSentAt: this.inflightOp.originalSentAt,
              retries: this.inflightOp.retries,
              docId: this.id,
              seq: this.inflightOp.seq,
              operationId: getOperationId(this.inflightOp),
            });

            if (
              this.inflightOp.retries > MAX_RETRIES ||
              isPast(addMinutes(this.inflightOp.originalSentAt, MAX_MINUTES_IN_FLIGHT))
            ) {
              rollbarAndLogError("ShareDB op stuck in flight too long", {
                originalSentAt: this.inflightOp.originalSentAt,
                retries: this.inflightOp.retries,
                docId: this.id,
              });

              // Clear the op that is stuck
              this._clearInflightOp();

              toast.error(t("error.toasts.save_failure"));
            }
          }

          this._sendOp();
        }
      };
    }

    // // TODO: You can comment this back in if you want to test acknowledgments
    // // that fail every other time
    // // Global variable to simulate intermittent failures
    // if (typeof window !== "undefined" && !window.__shareDBTestFailEveryOther) {
    //   window.__shareDBTestFailEveryOther = true;
    // }

    // if (!newDoc.__opAcknowledged) {
    //   newDoc.__opAcknowledged = newDoc._opAcknowledged;

    //   // Override _opAcknowledged to simulate intermittent failures
    //   newDoc._opAcknowledged = function _opAcknowledged(this: ExtendedDoc, message: any) {
    //     // Toggle the failure flag every time
    //     if (typeof window !== "undefined") {
    //       window.__shareDBTestFailEveryOther = !window.__shareDBTestFailEveryOther;
    //     }

    //     // If the flag is false, simulate failure by returning early
    //     if (typeof window !== "undefined" && !window.__shareDBTestFailEveryOther) {
    //       console.log("debug_sharedb_silent_failures", "Simulating opAcknowledged failure", {
    //         docId: this.id,
    //         message,
    //         failFlag: window.__shareDBTestFailEveryOther,
    //       });
    //       return; // Simulate failure by not calling the original method
    //     }

    //     // Call the original _opAcknowledged method
    //     console.log("debug_sharedb_silent_failures", "Allowing opAcknowledged to proceed", {
    //       docId: this.id,
    //       message,
    //       failFlag: window.__shareDBTestFailEveryOther,
    //     });

    //     return newDoc.__opAcknowledged.call(this, message);
    //   };
    // }

    // The ShareDB `doc` object is actually a function (not a class), which means we can't substitute
    // and methods on individual instances, rather, the changes below affect _all_ current and future
    // doc instances, hence, we make sure to only do it once.
    type ShareDBDocPrototype = Doc & {
      __submissionTrackerTryComposePatched?: boolean;
      _tryCompose?: (op: ExtendedOp) => ExtendedOp | undefined;
    };
    const docPrototype = Doc.prototype as ShareDBDocPrototype;
    if (!docPrototype.__submissionTrackerTryComposePatched) {
      const originalTryCompose = docPrototype._tryCompose;
      if (!originalTryCompose) {
        throw new Error("ShareDB Doc.prototype._tryCompose is missing");
      }
      docPrototype._tryCompose = function tryComposeWithSubmissionTracking(
        this: ExtendedDoc,
        op: ExtendedOp,
      ): ExtendedOp | undefined {
        const composed = originalTryCompose.call(this, op) as ExtendedOp | undefined;
        if (composed) {
          const mergedOperationId = getOperationId(op);
          const survivorOperationId = getOperationId(composed);
          if (
            mergedOperationId &&
            survivorOperationId &&
            mergedOperationId !== survivorOperationId
          ) {
            handleSubmissionComposed(mergedOperationId, survivorOperationId, this);
          }
        }
        return composed;
      };
      docPrototype.__submissionTrackerTryComposePatched = true;
    }

    if (!newDoc._submitOp) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      newDoc._submitOp = newDoc.submitOp;

      newDoc.submitOp = function submitOp(
        this: ExtendedDoc,
        op: ExtendedOp | ExtendedOp[],
        options: ShareDBSourceOptions,
        callback: Callback,
      ) {
        if (typeof options === "function") {
          throw new Error("second argument to submitOp can not be a callback");
        }

        // doc.type is null in three cases: (1) before the initial snapshot arrives from the
        // server, (2) during _hardRollback recovery while the re-fetch is in flight, and
        // (3) after the doc is deleted. Submitting in any of these states would throw
        // ERR_DOC_DOES_NOT_EXIST in ShareDB's _submit — the op can't apply and would be lost
        // anyway, so skip to avoid spurious errors.
        if (!this.type) {
          logInfoWithDebugFlag("debug_sharedb", "submitOp skipped: doc not ready", {
            docId,
            op,
          });
          return;
        }

        // Create submission tracker for this operation
        const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Add unique ID to the operation
        const ops = Array.isArray(op) ? op : [op];
        ops.forEach((operation) => {
          if (!operation.operationId) {
            // eslint-disable-next-line no-param-reassign
            operation.operationId = operationId;
          }
        });

        checkOpPermissions(newDoc, op);

        // Here we start a submission tracker to ensure we can track the status of this operation
        // and throw an error if it never completes.
        createSubmissionTracker(this, operationId);

        logInfoWithDebugFlag("debug_sharedb_silent_failures", `Tracker created,`, {
          operationId,
          op,
        });

        dispatch(viewStateActions.onSendShareDBOp());

        this._submitOp(op, options, (error: WebsocketErrorType) => {
          if (error) {
            const state = store.getState();
            const fieldLosses =
              this.data &&
              reportUncommittedWorksheetFieldTextLoss({
                docId: this.id,
                doc: this.data,
                operationId,
                relatedOperationIds: getRelatedOperationIds(operationId),
                cause: "submit-error",
                userId: selectCurrentUserId(state) ?? state.auth.websocket?.user_id ?? undefined,
                sectionId: shareDBSectionId ?? undefined,
                errorMessage: error.message,
              });

            rollbarAndLogError(
              `[ShareDBDoc] Error submitting op for doc ${docId}`,
              enrichShareDBFailureLog(
                { docId, operationId, error: error.message },
                fieldLosses ?? [],
              ),
            );
            dispatch(viewStateActions.onShareDBError(error.message));
            clearSubmissionTrackerById(operationId, { cause: "submit-error" });
          } else {
            updateDraftPublication();
          }

          dispatch(viewStateActions.onReceiveShareDBOp());

          incrementVersion(this, op);

          if (callback) {
            callback(error);
          }
        });
      };
    }

    const state = store.getState();
    const pageId = selectPageIdByWorksheetId(state, docId);
    const { canViewPage } = selectPageCapabilities(state, pageId);
    newDoc.subscribe((err) => {
      if (err) {
        const websocket = store.getState().auth.websocket;
        const reason = getDocErrorReason(docId, canViewPage, "subscribe");
        rollbarAndLogError("[ShareDBDoc] Error subscribing to doc", {
          docId,
          user_id: websocket?.user_id,
          err: err.message,
          reason,
        });
      } else if (newDoc.type === null) {
        rollbarAndLogWarning("[ShareDBDoc] doc.subscribe called but no type set", { docId });
      }
      logInfoWithDebugFlag(
        "debug_sharedb_subscriptions",
        `[ShareDBDoc] Subscribed to doc ${docId}`,
      );
    });

    return unsubscribe;
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [docId, shareDBConnection, shareDBSectionId, dispatch, maybeRerender, updateDraftPublication]);

  if (doc && !doc.data) {
    // If the doc breaks in unexpected ways, doc.data will be undefined.
    // TODO: We might want to separate the two cases to distinguish between error and loading states.
    if (previousDataStateRef.current !== "undefined") {
      logInfoWithDebugFlag("debug_sharedb", `[useWorksheet] Doc data is undefined`, {
        docId,
        caller: callerRef.current,
      });
      previousDataStateRef.current = "undefined";
    }
    return { doc: null };
  }

  if (doc && doc.data) {
    if (previousDataStateRef.current !== "ready") {
      logInfoWithDebugFlag("debug_sharedb", `[useWorksheet] Doc is ready`, {
        docId,
        caller: callerRef.current,
      });
      previousDataStateRef.current = "ready";
    }
    return { doc };
  }

  if (previousDataStateRef.current !== null) {
    logInfoWithDebugFlag("debug_sharedb", `[useWorksheet] Doc is null`, {
      docId,
      caller: callerRef.current,
    });
    previousDataStateRef.current = null;
  }
  previousDataStateRef.current = null;
  return { doc: null };
};

// ============================================================================
// Change Detection Helpers
// ============================================================================
// These functions determine when a component should re-render based on specific
// changes to the ShareDB document. They are used by specialized hooks to optimize
// re-rendering behavior.

/**
 * Helper function to check if blocks structure changed (blocks added/removed/type changed).
 * This ignores content changes within blocks.
 */
const blocksStructureChanged = (
  currentDoc: Doc<WorksheetType> | null,
  previousDoc: Doc<WorksheetType> | null,
): boolean => {
  if (!currentDoc?.data || !previousDoc?.data) {
    // If either doc is missing, consider it a change (initial load or doc deleted)
    return currentDoc?.data !== previousDoc?.data;
  }

  const currentBlocks = currentDoc.data.b.map((b) => ({ id: b.id, t: b.t }));
  const previousBlocks = previousDoc.data.b.map((b) => ({ id: b.id, t: b.t }));

  if (currentBlocks.length !== previousBlocks.length) {
    return true;
  }

  // Check if any block IDs or types changed
  return !currentBlocks.every(
    (block, index) =>
      block.id === previousBlocks[index]?.id && block.t === previousBlocks[index]?.t,
  );
};

/**
 * Helper function to check if hasData for a specific accessId changed or if blocks changed.
 * Blocks changes matter because they affect which fields exist (and thus which data is considered valid).
 * Only re-renders when hasData changes (true -> false or false -> true), not when data changes but hasData stays the same.
 */
const accessIdHasDataChanged =
  (accessId: string) =>
  (currentDoc: Doc<WorksheetType> | null, previousDoc: Doc<WorksheetType> | null): boolean => {
    // If blocks changed, we need to re-render (affects fieldIds, which affects hasData)
    if (blocksStructureChanged(currentDoc, previousDoc)) {
      return true;
    }

    if (!currentDoc?.data || !previousDoc?.data) {
      return currentDoc?.data !== previousDoc?.data;
    }

    // Check if hasData changed (not just if data changed)
    const currentHasData = accessIdHasData(currentDoc, accessId);
    const previousHasData = accessIdHasData(previousDoc, accessId);

    return currentHasData !== previousHasData;
  };

/**
 * Helper function to check if doc loaded state changed (null -> doc or doc -> null).
 */
const docLoadedChanged =
  () =>
  (currentDoc: Doc<WorksheetType> | null, previousDoc: Doc<WorksheetType> | null): boolean => {
    const currentLoaded = Boolean(currentDoc?.data);
    const previousLoaded = Boolean(previousDoc?.data);
    return currentLoaded !== previousLoaded;
  };

/**
 * Helper function to check if doc version has diverged from a reference version.
 * Only re-renders when the divergence status changes (true -> false or false -> true),
 * not when doc version changes but the divergence status stays the same.
 */
const docVersionDivergedChanged =
  (referenceVersion: number | undefined) =>
  (currentDoc: Doc<WorksheetType> | null, previousDoc: Doc<WorksheetType> | null): boolean => {
    if (!referenceVersion) {
      // No reference version, so divergence is always false
      return false;
    }

    const currentDiverged = (currentDoc?.version ?? 0) !== referenceVersion;
    const previousDiverged = (previousDoc?.version ?? 0) !== referenceVersion;

    return currentDiverged !== previousDiverged;
  };

/**
 * Helper function to check if hasBlocks changed.
 * Only re-renders when blocks are added/removed (not when content changes).
 */
const hasBlocksChanged =
  () =>
  (currentDoc: Doc<WorksheetType> | null, previousDoc: Doc<WorksheetType> | null): boolean => {
    if (!currentDoc?.data || !previousDoc?.data) {
      return currentDoc?.data !== previousDoc?.data;
    }

    // Check if hasBlocks changed
    const currentHasBlocks = hasBlocks(currentDoc);
    const previousHasBlocks = hasBlocks(previousDoc);

    return currentHasBlocks !== previousHasBlocks;
  };

/**
 * Helper function to check if accessIds in worksheet.d changed (keys added/removed).
 * Only re-renders when accessIds change, not when data within accessIds changes.
 */
const worksheetAccessIdsChanged =
  () =>
  (currentDoc: Doc<WorksheetType> | null, previousDoc: Doc<WorksheetType> | null): boolean => {
    if (!currentDoc?.data || !previousDoc?.data) {
      return currentDoc?.data !== previousDoc?.data;
    }

    // Check if the keys (accessIds) in worksheet.d changed
    const currentAccessIds = new Set(Object.keys(currentDoc.data[DATA_PATH] || {}));
    const previousAccessIds = new Set(Object.keys(previousDoc.data[DATA_PATH] || {}));

    if (currentAccessIds.size !== previousAccessIds.size) {
      return true;
    }

    return ![...currentAccessIds].every((id) => previousAccessIds.has(id));
  };

// ============================================================================
// Specialized Hooks
// ============================================================================
// These hooks use useWorksheet with specific change detection strategies to
// optimize re-rendering. They only re-render when the specific value they return
// changes, not on every ShareDB operation.

/**
 * Hook that subscribes to worksheet changes but only re-renders when doc loaded state changes.
 * Returns whether the doc is loaded (has data).
 * Useful for components that only care about when the doc finishes loading.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useWorksheetLoaded = (docId: string | undefined): boolean => {
  const { doc } = useWorksheet(docId, docLoadedChanged());
  return Boolean(doc?.data);
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when hasData for a specific accessId changes
 * (true -> false or false -> true) or when blocks change (which affects which fields exist).
 * Returns whether the specified accessId has data.
 * Useful for components that only care about whether a specific accessId has data.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useAccessIdHasData = (docId: string | undefined, accessId: string): boolean | null => {
  const { doc } = useWorksheet(docId, accessIdHasDataChanged(accessId));
  if (!doc) return null;
  return accessIdHasData(doc, accessId);
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when hasBlocks changes
 * (true -> false or false -> true), not when content within blocks changes.
 * Returns whether the worksheet has any blocks.
 * Useful for components that only care about whether the doc has blocks.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useWorksheetHasBlocks = (docId: string | undefined): boolean | null => {
  const { doc } = useWorksheet(docId, hasBlocksChanged());
  if (!doc) return null;
  return hasBlocks(doc);
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when accessIds in worksheet.d change
 * (keys added/removed), not when data within those accessIds changes.
 * Returns the accessIds (keys of worksheet.d).
 * Useful for components that only care about which accessIds exist, not their content.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useWorksheetAccessIds = (docId: string | undefined): string[] => {
  const { doc } = useWorksheet(docId, worksheetAccessIdsChanged());
  if (!doc?.data) return [];
  return Object.keys(doc.data[DATA_PATH] || {});
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when doc version divergence changes
 * (true -> false or false -> true), not when doc version changes but the divergence status stays the same.
 * Returns whether the doc version has diverged from the reference version.
 * Useful for components that only care about whether the doc has been edited since a reference point.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useDocVersionDiverged = (
  docId: string | null,
  referenceVersion: number | undefined,
): boolean => {
  const { doc } = useWorksheet(docId, docVersionDivergedChanged(referenceVersion));
  if (!referenceVersion) return false;
  return (doc?.version ?? 0) !== referenceVersion;
};

// ============================================================================
// Other Hooks
// ============================================================================

/**
 * Listens to permission changes and forces a data refresh on the document if the permissions
 * change in a meaningful way.
 */
export const useWorksheetPermissionRefresh = (pageId: string) => {
  const docId = useAppSelector((s) => selectWorksheetIdByPage(s, pageId));

  const { canViewPage, canViewOtherUsersData } = usePageCapabilitiesForPageId(pageId);
  const previousCanViewOtherUsersData = useRef<boolean>(canViewOtherUsersData);

  const pageGroupId = useAppSelector((s) => selectPageGroupIdForCurrentUserByPageId(s, pageId));
  const previousPageGroupId = useRef<string | null>(pageGroupId);

  const dispatch = useAppDispatch();
  const refreshTokenRef = useRef(0);

  // Use a layout effect so we can mark "refresh in-flight" before passive effects (e.g. worksheet init ops)
  // run in the same commit.
  useLayoutEffect(() => {
    if (!docId || !canViewPage) {
      previousCanViewOtherUsersData.current = canViewOtherUsersData;
      previousPageGroupId.current = pageGroupId;
      logInfoWithDebugFlag(
        "debug_sharedb",
        `[useWorksheetPermissionRefresh] Not refreshing doc because not released or no docId`,
        {
          docId,
          canViewPage,
        },
      );
      return;
    }
    const doc = docCache[docId];

    if (!doc || doc.id !== docId) {
      previousCanViewOtherUsersData.current = canViewOtherUsersData;
      previousPageGroupId.current = pageGroupId;
      logInfoWithDebugFlag(
        "debug_sharedb",
        `[useWorksheetPermissionRefresh] Not refreshing doc because no doc or docId changed`,
        {
          docId,
          doc,
        },
      );
      return;
    }

    const canViewOtherUsersDataChanged =
      previousCanViewOtherUsersData.current !== canViewOtherUsersData;
    const pageGroupIdChanged = previousPageGroupId.current !== pageGroupId;
    if (canViewOtherUsersDataChanged || pageGroupIdChanged) {
      logInfoWithDebugFlag(
        "debug_sharedb",
        `[useWorksheetPermissionRefresh] Permissions changed, forcing refresh`,
        {
          docId,
          canViewOtherUsersData,
          previousCanViewOtherUsersData: previousCanViewOtherUsersData.current,
          pageGroupId,
          previousPageGroupId: previousPageGroupId.current,
        },
      );
      refreshTokenRef.current += 1;
      const token = refreshTokenRef.current;
      dispatch(viewStateActions.setWorksheetIsRefreshing(true));
      forceRefreshDoc(docId, () => {
        // Don't clear if another refresh started after this one.
        if (refreshTokenRef.current === token) {
          dispatch(viewStateActions.setWorksheetIsRefreshing(false));
        }
      });
    }

    previousCanViewOtherUsersData.current = canViewOtherUsersData;
    previousPageGroupId.current = pageGroupId;
  }, [docId, dispatch, canViewPage, canViewOtherUsersData, pageGroupId]);
};
